import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Flex, Button, Text, Table, Thead, Tbody, Tfoot, Tr, Th, Td, Select, ButtonGroup, Input, IconButton, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Badge, VStack ,useDisclosure } from "@chakra-ui/react";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft } from 'react-icons/fa';

export default function MasterMenuMakanan({ onBack }) {
  const toast = useToast();
  const [menus, setMenus] = useState({});
  const [activeBMI, setActiveBMI] = useState('normal');
  const [activeVariant, setActiveVariant] = useState(1);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [form, setForm] = useState({
    id: '', kategori_bmi: 'normal', menu_variant: 1, waktu_makan: 'Sarapan', menu: '', kkal: ''
  });

  const showToast = (title, status = 'info') => {
    toast({ title, status, duration: 3000, isClosable: true, position: 'top' });
  };

  const bmiList = ['underweight','normal','overweight','obese1','obese2'];
  const waktuList = ['Sarapan','Snack Pagi','Makan Siang','Snack Sore','Makan Malam'];
  const currentMenus = menus[activeBMI]?.[activeVariant] || {};

  const loadMenus = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE}/get-menu?kategori_bmi=${activeBMI}`);
      setMenus(prev => ({...prev, [activeBMI]: res.data.menus || {}}));
    } catch (err) {
      showToast('Gagal load menu: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  useEffect(() => { loadMenus(); }, [activeBMI]);

  useEffect(() => {
    setForm(prev => ({...prev, kategori_bmi: activeBMI, menu_variant: activeVariant}));
  }, [activeBMI, activeVariant]);

  const handleSave = async () => {
    if (!form.menu ||!form.kkal) return showToast('Menu & Kkal wajib diisi', 'warning');
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE}/save-menu`, form);
      showToast('Menu berhasil disimpan', 'success');
      onClose();
      loadMenus();
      setForm({ id: '', kategori_bmi: activeBMI, menu_variant: activeVariant, waktu_makan: 'Sarapan', menu: '', kkal: '' });
    } catch (err) {
      showToast('Gagal simpan menu', 'error');
    }
  };

  const handleEdit = (waktu) => {
    const menuData = currentMenus[waktu];
    setForm({
      id: menuData?.id || '', kategori_bmi: activeBMI, menu_variant: activeVariant,
      waktu_makan: waktu, menu: menuData?.menu || '', kkal: menuData?.kkal || ''
    });
    onOpen();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus menu ini?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE}/delete-menu?id=${id}`);
      showToast('Menu berhasil dihapus', 'success');
      loadMenus();
    } catch (err) {
      showToast('Gagal hapus menu', 'error');
    }
  };

  const totalKkal = waktuList.reduce((sum, waktu) => sum + (parseInt(currentMenus[waktu]?.kkal) || 0), 0);
  const getTotalColor = () => {
    if (activeBMI === 'underweight') return 'green.600';
    if (activeBMI === 'normal' && totalKkal > 2200) return 'orange.600';
    if (['overweight','obese1','obese2'].includes(activeBMI) && totalKkal > 1800) return 'red.600';
    return 'blue.600';
  };
  const totalColor = getTotalColor();
  const isWarning = totalColor!== 'blue.600' && totalColor!== 'green.600';

  return (
    <Box p={4}>
      <Flex justify="space-between" mb={4} align="center">
        <Button leftIcon={<FaArrowLeft />} onClick={onBack} variant="ghost">Kembali</Button>
        <Text fontSize="xl" fontWeight="bold">Master Menu Makanan</Text>
        <Button leftIcon={<FaPlus />} colorScheme="purple" onClick={() => {
          setForm({ id: '', kategori_bmi: activeBMI, menu_variant: activeVariant, waktu_makan: 'Sarapan', menu: '', kkal: '' });
          onOpen();
        }}>Tambah Menu</Button>
      </Flex>

      <ButtonGroup mb={4} isAttached>
        {bmiList.map(bmi => (
          <Button key={bmi} colorScheme={activeBMI === bmi? 'blue' : 'gray'} onClick={() => setActiveBMI(bmi)} size="sm">{bmi.toUpperCase()}</Button>
        ))}
      </ButtonGroup>

      <ButtonGroup mb={4} spacing={2}>
        {[1,2,3].map(v => (
          <Button key={v} colorScheme={activeVariant === v? 'purple' : 'gray'} onClick={() => setActiveVariant(v)} size="sm">Variant {v}</Button>
        ))}
      </ButtonGroup>

      <Text fontSize="md" mb={4}>Kategori: <Badge colorScheme="blue">{activeBMI}</Badge> | Variant: <Badge colorScheme="purple">{activeVariant}</Badge></Text>

      <Box overflowX="auto" borderWidth="1px" borderRadius="lg">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr><Th>Waktu Makan</Th><Th>Menu</Th><Th>Kkal</Th><Th width="100px">Aksi</Th></Tr>
          </Thead>
          <Tbody>
            {waktuList.map(waktu => {
              const menu = currentMenus[waktu];
              return (
                <Tr key={waktu}>
                  <Td fontWeight="bold">{waktu}</Td>
                  <Td>{menu?.menu || <Text color="gray.400">Belum diisi</Text>}</Td>
                  <Td>{menu?.kkal || '-'}</Td>
                  <Td>
                    <IconButton icon={<FaEdit />} size="xs" mr={2} onClick={() => handleEdit(waktu)} />
                    {menu?.id && <IconButton icon={<FaTrash />} size="xs" colorScheme="red" onClick={() => handleDelete(menu.id)} />}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
          <Tfoot bg={isWarning? 'red.50' : 'blue.50'}>
            <Tr><Th colSpan={2} textAlign="right">Total Kalori Harian:</Th><Th fontSize="xl" color={totalColor}>{totalKkal} kkal {isWarning && <Text fontSize="xs" as="span" ml={2}>⚠️ Kebanyakan</Text>}</Th><Th></Th></Tr>
          </Tfoot>
        </Table>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Menu {activeBMI.toUpperCase()} - Variant {activeVariant}</ModalHeader>
          <ModalBody>
            <Select mb={3} value={form.kategori_bmi} onChange={e => setForm({...form, kategori_bmi: e.target.value})}>{bmiList.map(b => <option key={b} value={b}>{b.toUpperCase()}</option>)}</Select>
            <Select mb={3} value={form.menu_variant} onChange={e => setForm({...form, menu_variant: parseInt(e.target.value)})}><option value={1}>Variant 1</option><option value={2}>Variant 2</option><option value={3}>Variant 3</option></Select>
            <Select mb={3} value={form.waktu_makan} onChange={e => setForm({...form, waktu_makan: e.target.value})}>{waktuList.map(w => <option key={w} value={w}>{w}</option>)}</Select>
            <Input mb={3} placeholder="Nama Menu" value={form.menu} onChange={e => setForm({...form, menu: e.target.value})} />
            <Input placeholder="Kalori Kkal" type="number" value={form.kkal} onChange={e => setForm({...form, kkal: e.target.value})} />
          </ModalBody>
          <ModalFooter><Button onClick={onClose} mr={3}>Batal</Button><Button colorScheme="purple" onClick={handleSave}>Simpan</Button></ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}